import React, { useState, useEffect } from 'react';
import {
  Shield, Info, ArrowClockwise, Check, X, ArrowRight, Lightning,
} from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode, getTickets as fetchTickets,
  getsubscribername as fetchSubscriberName,
  deletelegalcell as deleteLegalCell, saveData as saveAuditLog,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function LegalCellPermission() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [subscriberName, setSubscriberName] = useState('');
  const [legalCellName, setLegalCellName] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [reason, setReason] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [groupCode, setGroupCode] = useState(null);
  const [, setChitgroupId] = useState(null);
  const [selectedTicketNo, setSelectedTicketNo] = useState(null);
  const [selectedBranchCode, setSelectedBranchCode] = useState(null);

  // Captures the checkbox state at the moment the ticket was loaded, so we
  // have a real "old" value to compare against at save time.
  const [initialCheckedState, setInitialCheckedState] = useState(false);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setSelectedTicketNo(null);
    setInitialCheckedState(false);
    setSubscriberName('');
    setLegalCellName('');
    setIsChecked(false);
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setInitialCheckedState(false);
    setTicket('');
    resetFromTicket();
    setTickets([]);
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setSelectedBranchCode(null);
    setInitialCheckedState(false);
    setGroup('');
    resetFromGroup();
    setBranchCodeList([]);
    setTickets([]);
  };

  const loadGroups = async (branch) => {
    const schema = branch.branch_code;
    setBranchschema(schema);
    setSelectedBranchCode(branch.branch_code);
    try {
      const res = await fetchGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const loadTickets = async (schema, gCode) => {
    try {
      const res = await fetchTickets(schema, gCode);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) loadGroups(selected);
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (selected) {
      setGroupCode(selected.group_Code);
      setChitgroupId(selected.chitgroup_id);
      loadTickets(branchschema, selected.group_Code);
    }
  };

  const handleTicketChange = async (value) => {
    setTicket(value || '');
    resetFromTicket();
    if (!value) return;
    const selected = tickets.find((x) => x.ticketno === value);
    if (!selected) return;

    setSelectedTicketNo(selected.ticketno);

    // Ticked + save grants (vchstatus 'f' -> 't'); unticked + save revokes ('t' -> 'f').
    setIsChecked(false);
    setInitialCheckedState(false);

    try {
      const res = await fetchSubscriberName(branchschema, selected.ticketno, groupCode, selectedBranchCode);
      const name = res && res.length > 0 ? res[0].subscriber_name : '';
      setSubscriberName(name);
      setLegalCellName(res && res.length > 0 ? (res[0].vchlegal || '') : '');
      // vchstatus true ('t') -> ticked, false ('f') -> unticked
      const status = res && res.length > 0 ? String(res[0].vchstatus || '').toLowerCase() : '';
      const granted = status === 't' || status === 'true';
      setIsChecked(granted);
      setInitialCheckedState(granted);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('getsubscribername failed:', err);
    }
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const reasonInvalid = touched && (reason.length < 10 || reason.length > 200);

  const isValid = !!cao && !!group && !!ticket && !!selectedTicketNo
    && reason.length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    setPendingChanges([
      {
        label: 'Allow Legal Receipt',
        // Old value is the vchstatus loaded with the ticket.
        oldValue: initialCheckedState ? 'Granted' : 'Not Granted',
        newValue: isChecked ? 'Granted' : 'Not Granted',
      },
    ]);
    setPendingReason(reason);

    setPendingUpdatePayload({
      groupCode,
      ticketNo: selectedTicketNo,
    });

    setPendingAudit({
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'LEGALCELL_PERMISSION',
      oldData: { subscriberName, legalCellName, allowLegalReceipt: initialCheckedState },
      newData: { subscriberName, legalCellName, allowLegalReceipt: isChecked },
      reason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
    setPendingChanges([]);
  };

  const confirmSave = async () => {
    if (!pendingUpdatePayload || !pendingAudit) return;

    setSaving(true);
    try {
      const result = await deleteLegalCell(groupCode, selectedTicketNo, isChecked);
      const rows = result?.rowsAffected ?? result?.RowsAffected;
      if (rows === 0) {
        setSaving(false);
        setShowConfirmModal(false);
        showToast(
          'warn',
          'No Change',
          isChecked
            ? 'No record in the revoked state was found for this ticket, so nothing was granted.'
            : 'No record in the granted state was found for this ticket, so nothing was revoked.',
          true,
        );
        return;
      }
      try {
        await saveAuditLog([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Legal cell permission updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Saved, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Save failed:', err);
      showToast('error', 'Save Failed', 'Could not save the change. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
    setPendingChanges([]);
    setPendingReason('');
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="legalcell-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="legalcell-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Legal Cell Permission</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code, and ticket to load subscriber details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="legalcell-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!cao}
              error={groupInvalid}
              testId="legalcell-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group first"
              disabled={!group}
              error={ticketInvalid}
              testId="legalcell-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!selectedTicketNo} hint="Select branch, group code and ticket in section A first">
        {/* SECTION B: Subscriber & Permission */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Subscriber &amp; Permission</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Tick Allow Legal Receipt to grant the permission, leave it unticked to revoke it</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}>
              Subscriber Name
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>Read-only</span>
            </label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={subscriberName || ''} readOnly data-testid="legalcell-subscriber-name" />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}>
              Legal Cell Name
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>Read-only</span>
            </label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={legalCellName || ''} readOnly data-testid="legalcell-name" />
          </div>
        </div>

        {/* Native checkbox, same as the Angular version — no custom widget */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            id="allowLegalReceipt"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#0F766E' }}
            data-testid="legalcell-checkbox"
          />
          <label htmlFor="allowLegalReceipt" style={{ margin: 0, fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>
            Allow Legal Receipt
          </label>
          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>
            <Lightning size={10} weight="fill" /> Auto
          </span>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!selectedTicketNo} hint="Select branch, group code and ticket in section A first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="legalcell-reason"
        />
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Minimum 10 characters required</div>}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="legalcell-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="legalcell-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="legalcell-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Shield size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Legal Cell Permission Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update legal cell permission for this ticket. Please review before confirming.
              </p>

              <p style={{ fontSize: '12px', color: '#374151', margin: '0 0 12px' }}>
                Legal Cell: <strong>{pendingAudit?.newData?.legalCellName || '—'}</strong>
                {' · '}Subscriber: <strong>{pendingAudit?.newData?.subscriberName || '—'}</strong>
              </p>

              {pendingChanges.map((change) => (
                <div key={change.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (Old)</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{change.oldValue}</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (New)</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{change.newValue}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="legalcell-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="legalcell-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LegalCellPermission;